import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, updateAccount } from "../../lib/api";
import { useAccountFavs } from "../../lib/useAccountFavs";
import { TeamGrid } from "./UserSetup";
import { useAuth } from "../../lib/AuthContext";

const LEAGUES = [["pwhl", "PWHL"], ["nhl", "NHL"], ["international", "National Teams"], ["ncaa", "NCAA"]];

function IntlGrid({ isOn, onPick, order }) {
    const { data } = useQuery({ queryKey: ["intl-teams"], queryFn: () => api.get("/intl/teams").then((r) => r.data), staleTime: 60 * 60 * 1000 });
    return (
        <div className="grid grid-cols-5 gap-1.5" data-testid="account-fav-intl-grid">
            {(data?.teams || []).map((t) => (
                <button key={t.code} type="button" title={t.name} onClick={() => onPick(t.code)} data-testid={`account-fav-${t.code}`}
                    className={`relative aspect-square rounded-lg border flex items-center justify-center transition-[opacity,background-color,border-color] ${isOn(t.code) ? "bg-[#9D4CDD]/25 border-[#9D4CDD]" : "bg-black/30 border-white/10 opacity-50 grayscale hover:opacity-100 hover:grayscale-0"}`}>
                    <img src={`https://flagcdn.com/w80/${t.iso}.png`} alt={t.code} className="h-5 w-8 object-cover rounded-sm" />
                    {order(t.code)}
                </button>
            ))}
        </div>
    );
}

// Account favourites: tap teams in order — 1st, 2nd, 3rd… per league. Separate from the per-page team sort.
export default function FavouritesSection({ defaultLeague = "pwhl" }) {
    const qc = useQueryClient();
    const { isOwner } = useAuth();
    const saved = useAccountFavs();
    const [league, setLeague] = useState(["nhl", "international", "ncaa"].includes(defaultLeague) ? defaultLeague : "pwhl");
    const [sel, setSel] = useState(null);
    const favs = sel || saved;
    const { data: ncaaTeams = [] } = useQuery({ queryKey: ["ncaa-teams"], queryFn: () => api.get("/ncaaw/teams").then((r) => r.data), staleTime: 6 * 60 * 60 * 1000, enabled: league === "ncaa" });
    const list = favs[league] || [];
    const isOn = (id) => list.includes(id);
    const pick = (id) => {
        const nextList = isOn(id) ? list.filter((x) => x !== id) : [...list, id];
        const next = { ...favs, [league]: nextList };
        setSel(next);
        updateAccount({ fav_teams: next, main_team_id: next.pwhl[0] || "", main_nhl_team_id: next.nhl[0] || "" })
            .then(() => qc.invalidateQueries({ queryKey: ["account"] })).catch(() => { });
    };
    const order = (id) => (isOn(id) ? <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-0.5 rounded-full bg-[#F6C453] text-black text-[9px] font-black flex items-center justify-center" data-testid={`account-fav-rank-${id}`}>{list.indexOf(id) + 1}</span> : null);
    return (
        <div className="mx-auto max-w-md space-y-3" data-testid="account-favourites">
            <p className="text-xs text-white/50 text-center">Tap teams in order: 1st favourite, 2nd, 3rd… They show first and in team colour on the schedule.</p>
            <div className="flex gap-1.5" data-testid="account-fav-league">
                {LEAGUES.filter(([k]) => k !== "ncaa" || isOwner).map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setLeague(k)} data-testid={`account-fav-league-${k}`}
                        className={`flex-1 px-3 py-2 rounded-xl border text-[11px] font-black uppercase tracking-wider transition-colors ${league === k ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "bg-black/30 border-white/10 text-white/70 hover:text-white"}`}>{l}</button>
                ))}
            </div>
            {league === "international" ? <IntlGrid isOn={isOn} onPick={pick} order={order} /> : (
                <div className="relative">
                    <TeamGrid league={league} isOn={isOn} onPick={pick} testPrefix="account-fav" />
                </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-1 text-[10px] font-bold text-white/50" data-testid="account-fav-count">
                {list.length ? list.map((id, i) => <span key={id} className="px-1.5 py-0.5 rounded bg-white/[0.06] text-white/80">{i + 1}. {league === "ncaa" ? (ncaaTeams.find((t) => t.id === id)?.abbr || id) : String(id).toUpperCase()}</span>) : "No favourites yet"}
            </div>
        </div>
    );
}
